// Press and hold to see something secret; it hides again the moment you let go, switch apps or
// the phone locks, so a glance from a neighbour never catches it left open.
import { useEffect, useRef, useState, type ReactNode } from "react";

const HOLD_MS = 300;

export function HoldToReveal({
  children,
  label = "Hold to reveal your role",
  onRevealed,
}: {
  children: ReactNode;
  label?: string;
  onRevealed?: () => void;
}) {
  const [shown, setShown] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setShown(false);
  };

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState !== "visible") hide();
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("blur", hide);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("blur", hide);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const start = () => {
    if (timer.current) return;
    timer.current = setTimeout(() => {
      setShown(true);
      onRevealed?.();
    }, HOLD_MS);
  };

  return (
    <div
      className="hold-reveal"
      role="button"
      tabIndex={0}
      aria-label={label}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        start();
      }}
      onPointerUp={hide}
      onPointerCancel={hide}
      onLostPointerCapture={hide}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") start();
      }}
      onKeyUp={hide}
      onContextMenu={(e) => e.preventDefault()}
      style={{ touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}
    >
      {shown ? (
        children
      ) : (
        <div className="card hold-cover">
          <div className="hold-eye" aria-hidden>
            ◉
          </div>
          <p>{label}</p>
          <p className="small muted">Shield the screen, then press and hold.</p>
        </div>
      )}
    </div>
  );
}
